import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Music, Play, Pause, Upload, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { musicAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

/**
 * Music section for the AutoPost settings page.
 *
 * User feedback (dogfooded for a few days): auto-post renders were
 * silent because the AutoPostSettings page had no music UI. Only
 * the onboarding MusicStep set the field once. Users couldn't change
 * music selection, add tracks, or rotate through favourites after
 * the wizard.
 *
 * This component mirrors MusicStep's preset grid + upload pattern but
 * is sized for the settings-page context (compact card, header inline
 * with the rest of the settings sections rather than a centered
 * wizard headline). Lifts the same API endpoints (`musicAPI.getPresets`
 * + `musicAPI.upload`). No new backend surface needed.
 *
 * Behavior:
 *   - Multi-select preset toggles like MusicStep
 *   - Optional MP3 upload joins the same random-rotation pool
 *   - The scheduler picks one randomly per render (already wired in
 *     scheduler_service._generate_video_for_item)
 *   - "Keine Musik" clears both
 *
 * Props
 *   selectedPresetIds  string[]
 *   selectedUploadUrl  string | null
 *   onChange({ background_music_preset_ids, background_music_url })
 *    . Fires on every toggle/upload/clear, parent persists via
 *       autoPostAPI.updateConfig on the global Save button.
 */
export default function AutoPostMusicSection({
  selectedPresetIds = [],
  selectedUploadUrl,
  onChange,
}) {
  const { t } = useTranslation();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await musicAPI.getPresets();
        if (cancelled) return;
        setPresets(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stop playback on unmount. Prevents an audio element from hanging
  // around after the user navigates away mid-preview.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const togglePlay = (preset) => {
    if (playingId === preset.id && audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(preset.url);
    audio.addEventListener('ended', () => setPlayingId(null));
    audio.play().catch(() => {
      toast.error(t('autoPost.music.playFailed') || 'Vorschau-Audio konnte nicht abgespielt werden.');
      setPlayingId(null);
    });
    audioRef.current = audio;
    setPlayingId(preset.id);
  };

  const togglePresetLike = (preset) => {
    const isLiked = selectedPresetIds.includes(preset.id);
    const next = isLiked
      ? selectedPresetIds.filter((id) => id !== preset.id)
      : [...selectedPresetIds, preset.id];
    onChange?.({
      background_music_preset_ids: next,
      background_music_url: selectedUploadUrl ?? null,
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await musicAPI.upload(form);
      const url = res.data?.file_url || res.data?.url;
      if (!url) throw new Error('no-url');
      onChange?.({
        background_music_preset_ids: selectedPresetIds,
        background_music_url: url,
      });
      toast.success(t('autoPost.music.uploadSuccess') || 'Eigene Musik hochgeladen');
    } catch (_) {
      toast.error(t('autoPost.music.uploadFailed') || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeUpload = () => {
    onChange?.({
      background_music_preset_ids: selectedPresetIds,
      background_music_url: null,
    });
  };

  const clearAll = () => {
    onChange?.({
      background_music_preset_ids: [],
      background_music_url: null,
    });
  };

  const totalSelected = selectedPresetIds.length + (selectedUploadUrl ? 1 : 0);

  return (
    <Card className="border-[#d8d0bd] bg-[#fffdf7]">
      <CardContent className="p-5 space-y-4">
        {/* Header. Matches the visual cadence of the other settings
            cards (Schedule, Platforms, etc.) so the music section feels
            like a peer rather than a foreign block. */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-[#23211a] flex items-center gap-2">
              <Music className="w-4 h-4 text-[#1f4d3a]" />
              {t('autoPost.music.title') || 'Hintergrundmusik'}
            </h3>
            <p className="text-xs text-[#6f6a5c] mt-0.5 max-w-md">
              {t('autoPost.music.subtitle') ||
                'Wähle deine Lieblings-Tracks. Pro Auto-Post spielen wir zufällig einen davon. So klingt nicht jedes Video gleich.'}
            </p>
          </div>
          {totalSelected > 0 && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#dfe8db] text-[#2e6b50] border border-[#1f4d3a]/30 whitespace-nowrap">
              {t('autoPost.music.selectedCount', { count: totalSelected }) ||
                `${totalSelected} ausgewählt`}
            </span>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 text-[#1f4d3a] animate-spin" />
          </div>
        )}

        {!loading && loadError && (
          <div className="flex items-start gap-2 text-xs text-[#9a6d1f] bg-[#f6edd8] border border-[#c7a869] rounded-lg p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {t('autoPost.music.loadError') ||
                'Presets konnten nicht geladen werden. Lade die Seite neu oder lade eine eigene Datei hoch.'}
            </span>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {/* Preset grid. Denser than MusicStep's 1-col/2-col since
                this lives inside a settings card alongside other
                sections. Mobile keeps 1 col, tablet+ goes 2. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {presets.map((preset) => {
                const isSelected = selectedPresetIds.includes(preset.id);
                const isPlaying = playingId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => togglePresetLike(preset)}
                    className={`text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'border-[#1f4d3a] bg-[#e9efe7]'
                        : 'border-[#d8d0bd] bg-[#f7f3ea]/40 hover:border-[#1f4d3a]/50'
                    }`}
                    data-testid={`autopost-music-${preset.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-[#23211a]">{preset.name}</p>
                      {isSelected && <Check className="w-4 h-4 text-[#1f4d3a] shrink-0" />}
                    </div>
                    <p className="text-[11px] text-[#6f6a5c] mt-0.5 mb-2 line-clamp-2">
                      {preset.description}
                    </p>
                    {/* Play/Pause control. Separate clickable region.
                        e.stopPropagation prevents the surrounding card
                        click from toggling like state when the user
                        just wanted to preview the audio. */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); togglePlay(preset); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePlay(preset);
                        }
                      }}
                      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border transition ${
                        isPlaying
                          ? 'border-[#1f4d3a] text-[#2e6b50] bg-[#dfe8db]'
                          : 'border-[#d8d0bd] text-[#4a463b] hover:border-[#1f4d3a]/50'
                      }`}
                    >
                      {isPlaying ? (
                        <><Pause className="w-3 h-3" /> {t('autoPost.music.pause') || 'Pause'}</>
                      ) : (
                        <><Play className="w-3 h-3" /> {t('autoPost.music.play') || 'Anhören'}</>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Upload. Sits beside presets in the rotation pool, not
                in place of them. If user has presets liked AND an
                upload, the scheduler picks across all. */}
            <div className="border-t border-[#e3dccb] pt-3 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#23211a]">
                    {t('autoPost.music.uploadTitle') || 'Eigene Musik hinzufügen'}
                  </p>
                  <p className="text-[11px] text-[#8a8474]">
                    {t('autoPost.music.uploadSubtitle') ||
                      'MP3 hochladen. Wird zur zufälligen Auswahl dazugepackt.'}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] w-full sm:w-auto sm:shrink-0"
                >
                  {uploading ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      {t('common.uploading') || 'Lade hoch…'}</>
                  ) : (
                    <><Upload className="w-3.5 h-3.5 mr-1.5" />
                      {t('autoPost.music.uploadBtn') || 'MP3 hochladen'}</>
                  )}
                </Button>
              </div>

              {selectedUploadUrl && (
                <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-[#1f4d3a]/40 bg-[#f0f3ec]">
                  <div className="flex items-center gap-2 min-w-0">
                    <Check className="w-3.5 h-3.5 text-[#1f4d3a] shrink-0" />
                    <span className="text-[11px] text-[#23211a] truncate">
                      {selectedUploadUrl.split('/').pop() || selectedUploadUrl}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeUpload}
                    className="text-[11px] text-[#6f6a5c] hover:text-[#23211a] shrink-0"
                  >
                    {t('common.remove') || 'Entfernen'}
                  </button>
                </div>
              )}
            </div>

            {/* "Keine Musik" escape. Clears every selection. The
                scheduler will then render silent (or voiceover-only
                if the template has voiceover enabled). */}
            {totalSelected > 0 && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[11px] text-[#8a8474] hover:text-[#4a463b] underline-offset-2 hover:underline"
                >
                  {t('autoPost.music.clearAll') || 'Keine Musik verwenden'}
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
